import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { profilePersistence } from "@/services/profilePersistence";

export interface ProfileCompletionStatus {
  isComplete: boolean;
  missingFields: string[];
  hasProfile: boolean;
  isPhoneVerified: boolean;
  profile?: any;
}

export function useProfileCompletion() {
  const { isAuthenticated, user } = useAuth();

  const { data: profileStatus, isLoading, error, refetch } = useQuery({
    queryKey: ["/api/profile/completion-status"],
    queryFn: async (): Promise<ProfileCompletionStatus> => {
      // First check local storage for quick response
      const cachedData = profilePersistence.getProfileCompletion();

      try {
        const response = await fetch("/api/profile", {
          credentials: "include",
        });

        if (!response.ok) {
          if (response.status === 404) {
            // No profile exists
            const result = {
              isComplete: false,
              missingFields: ["firstName", "lastName", "avatarName", "phoneNumber"],
              hasProfile: false,
              isPhoneVerified: false,
            };

            // Clear any stale cached data
            profilePersistence.clearProfileData();
            return result;
          }
          throw new Error("Failed to fetch profile");
        }

        const profile = await response.json();

        console.log('📊 useProfileCompletion: Fetched profile data:', profile);

        // Check required fields for profile completion
        // Note: firstName, lastName, phoneNumber come from the user object (merged in API response)
        const requiredFields = [
          { key: "firstName", label: "First Name" },
          { key: "lastName", label: "Last Name" },
          { key: "avatarName", label: "Avatar Name" },
          { key: "phoneNumber", label: "Phone Number" },
        ];

        const missingFields: string[] = [];

        for (const field of requiredFields) {
          if (!profile[field.key] || profile[field.key].trim() === "") {
            missingFields.push(field.label);
          }
        }

        // Check phone verification
        const isPhoneVerified = profile.isPhoneVerified === true;
        if (!isPhoneVerified) {
          missingFields.push("Phone Verification");
        }

        const isComplete = missingFields.length === 0;

        console.log('📊 useProfileCompletion: Profile completion check:', {
          isComplete,
          missingFields,
          hasProfile: !!profile.avatarName,
          isPhoneVerified,
          hasFirstName: !!profile.firstName,
          hasLastName: !!profile.lastName,
          hasAvatarName: !!profile.avatarName,
          hasPhoneNumber: !!profile.phoneNumber
        });

        const result = {
          isComplete,
          missingFields,
          hasProfile: !!profile.avatarName, // Profile exists if it has avatar name
          isPhoneVerified,
          profile,
        };

        // Sync with local storage
        profilePersistence.syncWithServerData(profile);

        return result;
      } catch (error) {
        // If server request fails, use cached data if available
        if (cachedData) {
          console.log("Using cached profile data due to server error:", error);
          return {
            isComplete: cachedData.isComplete,
            missingFields: cachedData.isComplete ? [] : ["Server connection failed"],
            hasProfile: !!cachedData.profileData,
            isPhoneVerified: cachedData.phoneVerified || false,
            profile: cachedData.profileData,
          };
        }
        throw error;
      }
    },
    enabled: isAuthenticated,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    profileStatus,
    isLoading,
    error,
    refetch,
    // Convenience getters
    isProfileComplete: profileStatus?.isComplete ?? false,
    hasProfile: profileStatus?.hasProfile ?? false,
    isPhoneVerified: profileStatus?.isPhoneVerified ?? false,
    missingFields: profileStatus?.missingFields ?? [],
  };
}
